import { timeAgo } from '../utils/events'
import { isUnconfigured } from '../utils/device'
import { useSettings } from '../api/settings'
import { TruckRowSkeleton } from './Skeleton'

export default function TruckList({ trucks, selected, onSelect, loading }) {
  const settings = useSettings()
  const DIESEL = settings.diesel

  if (loading) {
    return (
      <aside className="rail-card">
        <div className="rail-head">
          <h2>Trucks</h2>
        </div>
        <div>
          {[0, 1, 2, 3, 4, 5].map(i => <TruckRowSkeleton key={i} />)}
        </div>
      </aside>
    )
  }

  const fuelTier = l => l > 200 ? 'ok' : l > 50 ? 'low' : 'crit'

  // Efficiency score → dot colour. Null (no baseline yet) keeps the regular
  // online/alert dot — we don't want to show a misleading "red" on a brand-new
  // truck that simply hasn't accumulated 3 days of ledger data yet.
  const scoreColor = score => {
    if (score == null) return null
    if (score >= 80) return 'var(--tg-status-online)'
    if (score >= 60) return 'var(--tg-status-warning)'
    return 'var(--tg-status-danger)'
  }

  return (
    <aside className="rail-card">
      <div className="rail-head">
        <h2>Trucks</h2>
        <span style={{ fontSize: 'var(--tg-text-xs)', fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-muted)', background: 'var(--tg-bg-subtle)', padding: '2px 8px', borderRadius: 'var(--tg-radius-full)' }}>
          {trucks.length}
        </span>
      </div>

      <div style={{ maxHeight: 520, overflowY: 'auto' }}>
        {trucks.map(truck => {
          const pct = Math.min(100, (truck.fuel_mm / 500) * 100)
          // A truck without a valid 15-digit IMEI, or one provisioned but never
          // heard from ('never_connected'), has no real hardware behind it — render
          // it muted, never as a green/online dot, regardless of the online flag
          // (a stale ThingsBoard device id can still leak telemetry that flips it).
          const unconfigured = isUnconfigured(truck)
          const dotClass = unconfigured ? 'idle' : truck.hasAlert ? 'alert' : truck.online ? 'on' : 'off'
          const isAlert = !unconfigured && truck.hasAlert
          const sub = unconfigured
            ? (truck.connectivity === 'never_connected' ? 'Never connected' : 'No device')
            : truck.online
              ? (truck.speed > 2 ? Math.round(truck.speed) + ' km/h' : (truck.current_address || 'Stationary'))
              : 'Last seen ' + (truck.lastSeen ? timeAgo(truck.lastSeen) : 'unknown')
          // power_connected is admin-only; non-admin payloads omit the field so
          // this conditional naturally collapses. Show amber "On battery" when
          // the tracker is running on its LiPo (engine off / blown fuse / unplugged).
          const onBattery = !unconfigured && truck.online && truck.power_connected === false
          const fuelLabel = !unconfigured && (truck.online || isAlert) ? truck.fuel_liters.toFixed(0) + ' L' : '— L'

          const burn = truck.burn_rate_l_h
          const hte = truck.hours_to_empty
          const hteColor = hte != null
            ? (hte < 2 ? 'var(--tg-status-danger)' : hte < 4 ? 'var(--tg-status-warning)' : null)
            : null
          const dotColor = scoreColor(truck.efficiency_score)

          return (
            <div
              key={truck.id}
              className={`trow${isAlert ? ' is-alert' : ''}${selected === truck.id ? ' is-selected' : ''}`}
              onClick={() => onSelect(truck.id === selected ? null : truck.id)}
              title={truck.efficiency_score != null ? `Efficiency score: ${truck.efficiency_score}/100` : undefined}
            >
              <span
                className={`tdot ${dotClass}`}
                style={dotColor && !isAlert && !unconfigured ? { background: dotColor } : undefined}
              />
              <div className="tmid">
                <div className="tnm" style={unconfigured ? { color: 'var(--tg-text-muted)' } : undefined}>
                  {truck.name}
                  {truck.has_consumption_anomaly && (
                    <span
                      title={`Consumption ${truck.anomaly_severity} — above 7-day baseline`}
                      style={{
                        marginLeft: 6,
                        fontSize: 9,
                        fontWeight: 600,
                        padding: '1px 5px',
                        borderRadius: 4,
                        background: truck.anomaly_severity === 'high' ? 'var(--tg-danger-50)' : 'var(--tg-warning-50, #fef3c7)',
                        color: truck.anomaly_severity === 'high' ? 'var(--tg-danger-700)' : '#92400e',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      ⚠ {truck.anomaly_severity}
                    </span>
                  )}
                </div>
                <div className="tsub" title={truck.online && truck.speed <= 2 ? (truck.current_address || 'Stationary') : undefined}>{sub}</div>
                {onBattery && (
                  <div style={{ fontSize: 10, color: '#92400e', fontWeight: 600, marginTop: 2 }} title="Tracker running on internal backup battery — truck power lost">
                    ⚠ On battery
                  </div>
                )}
                {(burn != null || hte != null) && truck.online && !unconfigured && (
                  <div style={{ fontSize: 10, color: 'var(--tg-text-muted)', marginTop: 2 }}>
                    {burn != null && <span>Burning {burn.toFixed(1)}L/h</span>}
                    {burn != null && hte != null && hteColor && <span> · </span>}
                    {hte != null && hteColor && (
                      <span style={{ color: hteColor, fontWeight: 500 }}>
                        Empty in ~{hte < 1 ? '<1' : Math.round(hte)}h
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="tfuel">
                <div className="v">{fuelLabel}</div>
                <div className="bar">
                  <span style={{ width: (!unconfigured && (truck.online || isAlert) ? pct : 0) + '%', background: isAlert ? 'var(--tg-danger-500)' : 'var(--tg-teal-500)' }} />
                </div>
                {truck.refuel_prediction && (truck.refuel_prediction.urgency === 'critical' || truck.refuel_prediction.urgency === 'high') && (
                  <div
                    title={'Estimated empty: ' + new Date(truck.refuel_prediction.empty_at).toLocaleString()}
                    style={{
                      fontSize: 10, fontWeight: 600, marginTop: 4, textAlign: 'right', whiteSpace: 'nowrap',
                      color: truck.refuel_prediction.urgency === 'critical' ? 'var(--tg-danger-700)' : '#92400e',
                    }}
                  >
                    ⚠ Empty in {truck.refuel_prediction.hours_remaining < 1 ? '<1h' : Math.round(truck.refuel_prediction.hours_remaining) + 'h'}
                  </div>
                )}
                {truck.sensor_health?.alerts?.length > 0 && (
                  <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {truck.sensor_health.alerts.slice(0, 2).map((al, i) => {
                      const isHigh = al.severity === 'critical' || al.severity === 'high'
                      const short = al.type === 'ble_disconnected' ? 'BLE disconnected'
                        : al.type === 'ble_weak' ? 'BLE weak'
                        : al.type === 'battery_critical' ? 'Battery critical'
                        : al.type === 'battery_low' ? 'Battery low'
                        : al.type === 'gps_stale' ? 'GPS stale'
                        : al.type
                      return (
                        <span
                          key={i}
                          title={al.msg}
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            color: isHigh ? 'var(--tg-danger-700)' : '#92400e',
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ⚠ {short}
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </aside>
  )
}
